import { useRef } from "react";
import { IonModal, IonButton, useIonToast } from "@ionic/react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router";
import { dismissPopup } from "../../../redux/reducer/NotificationsSlice";
import { markRecipientsRead, markRecipientsReadByDelivery } from "../../../redux/actions";
import {
  handleNotificationAction,
  isChallengeConfirmAction,
} from "../../../services/notificationActions";
import ChallengeNotificationConfirm from "../../ChallengeNotificationConfirm";
import { withChallengeDatePrefix } from "../../../utils/notificationDate";
import "./NotificationPopup.css";

/**
 * In-app notification popup modal.
 * Renders dynamically from backend data (recipient object).
 *
 * Shown when a notification has `is_without_popup === false`.
 */
const NotificationPopup = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const [presentToast] = useIonToast();
  const confirmRef = useRef(null);

  const notification = useSelector((s) => s.notifications.pendingPopup);
  const isOpen = !!notification;

  const title = notification?.template?.attributes?.title || "";
  const body = notification?.template?.attributes?.body || "";
  const coverUrl = notification?.template?.attributes?.cover_url || null;
  // A post can have no image — the backend then returns the generic placeholder.
  // Treat that as "no cover" so the category chip renders as a block above the
  // title (not absolutely positioned over a collapsed/empty cover, where it would
  // overlap the title).
  const hasRealCover = !!coverUrl && !/placeholder/i.test(coverUrl);
  const actions = notification?.template?.attributes?.actions || [];

  // Category label = the notification topic's name (e.g. "Ricette sane"), shown
  // as a chip over the cover. Challenge topics carry an auto-generated name
  // (subscriber-challenge-<uuid>) and render their own "Sfida" pill instead, so
  // they're excluded here.
  const topicName =
    notification?.template?.attributes?.topic?.attributes?.name || "";
  const isChallengeTopic =
    /^subscriber-challenge/i.test(topicName) ||
    actions.some((a) => (a.style || a.attributes?.style) === "confirm");
  const categoryLabel = !isChallengeTopic ? topicName : "";

  // Challenge notifications: prefix the body (the question) with the day it was
  // sent — "Venerdì 10 luglio | …". The send time is stamped on the popup at
  // receipt (attributes.sent_at).
  const displayBody = isChallengeTopic
    ? withChallengeDatePrefix(body, notification?.attributes?.sent_at)
    : body;
  // Keep plain-text line breaks when rendering as HTML.
  const bodyHtml = /<[^>]+>/.test(displayBody)
    ? displayBody
    : displayBody.replace(/\n/g, "<br>");

  // Only mark as read if we have a real recipient ID (not a generated timestamp)
  const hasRealId = notification?.id && notification.id < 1e12;

  const markPopupRead = () => {
    if (notification?.deliver_id) {
      dispatch(markRecipientsReadByDelivery([notification.deliver_id]));
    } else if (hasRealId) {
      dispatch(markRecipientsRead([notification.id]));
    }
  };

  const handleDismiss = () => {
    markPopupRead();
    dispatch(dismissPopup());
  };

  const handleAction = async (action) => {
    markPopupRead();

    // Challenge "Conferma oggi" → open the in-app confirm modal (no redirect).
    const attrs = action?.attributes || action || {};
    if (isChallengeConfirmAction(action) && !attrs.disabled) {
      dispatch(dismissPopup());
      // Defer so the popup finishes dismissing before the confirm modal opens.
      setTimeout(() => confirmRef.current?.open(action), 60);
      return;
    }

    const result = await handleNotificationAction(action, history, {
      deliverId:
        notification?.deliver_id ?? notification?.attributes?.deliver_id ?? null,
    });

    if (result?.error) {
      presentToast({
        message: result.error,
        duration: 2500,
        color: "danger",
      });
    }

    dispatch(dismissPopup());
  };

  return (
    <>
    <IonModal
      isOpen={isOpen}
      className="notification-popup-modal"
      backdropDismiss={false}
      onDidDismiss={handleDismiss}
    >
      <div className="notification-popup">
        {/* Optional cover image with category chip overlaid top-left */}
        {hasRealCover && (
          <div style={{ position: "relative" }}>
            <img
              src={coverUrl}
              alt=""
              className="notification-popup__cover"
              onError={(e) => {
                e.target.style.display = "none";
              }}
            />
            {categoryLabel && (
              <span
                style={{
                  position: "absolute",
                  top: 10,
                  left: 10,
                  background: "#16A34A",
                  color: "#fff",
                  fontSize: "0.7rem",
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: "0.3px",
                  padding: "3px 8px",
                  borderRadius: 6,
                }}
              >
                {categoryLabel}
              </span>
            )}
          </div>
        )}

        <div className="notification-popup__body">
          {/* Category chip when there's no cover to overlay it on */}
          {!hasRealCover && categoryLabel && (
            <span
              style={{
                display: "inline-block",
                marginBottom: 8,
                background: "#16A34A",
                color: "#fff",
                fontSize: "0.7rem",
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.3px",
                padding: "3px 8px",
                borderRadius: 6,
              }}
            >
              {categoryLabel}
            </span>
          )}
          {/* Title */}
          {title && <h2 className="notification-popup__title">{title}</h2>}

          {/* Body text */}
          {body && (
            <p
              className="notification-popup__text notification-popup__text--highlight"
              dangerouslySetInnerHTML={{ __html: bodyHtml }}
            />
          )}

          {/* CTA buttons */}
          {actions.length > 0 && (
            <div className="notification-popup__actions">
              {actions.map((action, idx) => {
                // Challenge reminders carry a single standard "Conferma oggi"
                // action flagged style="confirm" — render it in the same orange
                // as the challenge card's CTA so the notification matches.
                const isConfirm =
                  (action.style || action.attributes?.style) === "confirm";
                // Challenge "Conferma oggi" is deactivated by the backend once
                // the activity for the day is already confirmed.
                const isDisabled = !!(
                  action.disabled ?? action.attributes?.disabled
                );
                return (
                  <IonButton
                    key={action.id || idx}
                    expand="block"
                    color={isConfirm || idx !== 0 ? undefined : "primary"}
                    fill="solid"
                    disabled={isDisabled}
                    className={
                      isConfirm
                        ? "notification-popup__cta-confirm"
                        : idx === 0
                        ? "notification-popup__cta-primary"
                        : "notification-popup__cta-secondary"
                    }
                    onClick={() => !isDisabled && handleAction(action)}
                  >
                    {action.attributes?.label || action.label || "Apri"}
                  </IonButton>
                );
              })}
            </div>
          )}

          {/* Close button — styled as outlined button matching mock */}
          <IonButton
            expand="block"
            fill="outline"
            color="medium"
            className="notification-popup__close-btn"
            onClick={handleDismiss}
          >
            Chiudi
          </IonButton>

          {/* Customize notifications link */}
          <button
            type="button"
            className="notification-popup__customize"
            onClick={() => {
              handleDismiss();
              history.push("/impostazioni-profilo", { tab: "notifiche" });
            }}
          >
            Personalizza notifiche
          </button>
        </div>
      </div>
    </IonModal>
    <ChallengeNotificationConfirm ref={confirmRef} />
    </>
  );
};

export default NotificationPopup;
